import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  getInitialStockSelection,
} from "./StocksOptions";
import {
  autoScrollSelectComponents,
} from "../Common/SelectMenuScroll";
import { formatCoTraderFilterLabel } from "./TickerUniverseOptions";

const COUNTRY_STORAGE_KEY = "default_country_stock";
const CATEGORY_STORAGE_KEY = "default_category_stock";
const SYMBOL_STORAGE_KEY = "default_symbol_stock";
const UPDATE_SPEED_STORAGE_KEY = "default_update_speed";

const DEFAULT_CATEGORY = "Equities";
const getCountryOptions = (stockOptions = {}) => Object.keys(stockOptions).map((country) => ({
  value: country,
  label: formatCoTraderFilterLabel(country),
}));

const UPDATE_SPEED_OPTIONS = [
  { value: 2, label: "2s" },
  { value: 3, label: "3s" },
  { value: 5, label: "5s" },
];

const normalizeCountry = (countryValue, stockOptions = {}) => {
  if (countryValue && stockOptions[countryValue]) {
    return countryValue;
  }

  return Object.keys(stockOptions)[0] || "";
};

const resolveCategoryForCountry = (
  countryValue,
  storedCategory,
  stockOptions = {},
) => {
  const categoryMap = stockOptions[countryValue] || {};
  const availableCategories = Object.keys(categoryMap);

  if (storedCategory && categoryMap[storedCategory]) {
    return storedCategory;
  }

  if (categoryMap[DEFAULT_CATEGORY]) {
    return DEFAULT_CATEGORY;
  }

  return availableCategories[0] || DEFAULT_CATEGORY;
};

const getSymbolsForSelection = (
  countryValue,
  categoryValue,
  stockOptions = {},
) => {
  const categoryMap = stockOptions[countryValue] || {};
  const symbols = categoryMap[categoryValue];

  return Array.isArray(symbols) ? symbols : [];
};

const resolveSymbolForSelection = (
  countryValue,
  categoryValue,
  storedSymbol,
  stockOptions = {},
) => {
  const symbols = getSymbolsForSelection(countryValue, categoryValue, stockOptions);
  const matchedSymbol = symbols.find((symbol) => symbol.value === storedSymbol);

  return matchedSymbol || symbols[0] || null;
};

const resolveUpdateSpeed = (storedValue) => {
  const parsedValue = Number(storedValue);
  const matchedSpeed = UPDATE_SPEED_OPTIONS.find(
    (option) => option.value === parsedValue,
  );

  return matchedSpeed || UPDATE_SPEED_OPTIONS[0];
};

const selectPortalStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 12,
    borderColor: state.isFocused ? "rgba(13, 110, 253, 0.35)" : "#D8DEE6",
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(13, 110, 253, 0.08)"
      : "none",
    "&:hover": {
      borderColor: state.isFocused ? "rgba(13, 110, 253, 0.35)" : "#C8D0DA",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: 10,
    paddingRight: 10,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: "#E5EAF1",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const CoTraderFilterStocks = ({
  stockOptions = {},
  defaultSymbol = "",
  setSelectedSymbol,
  setUpdateSpeed,
  setSelectedTickerLabel,
  onSelectionContextChange,
  isDisabled = false,
}) => {
  const countryOptions = useMemo(() => getCountryOptions(stockOptions), [stockOptions]);
  const initialSelection = useMemo(
    () => getInitialStockSelection(stockOptions, defaultSymbol),
    [defaultSymbol, stockOptions],
  );
  const [isLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    initialSelection?.country || null,
  );
  const [selectedCategory, setSelectedCategory] = useState(
    initialSelection?.category || null,
  );
  const [selectedSymbolOption, setSelectedSymbolOption] = useState(
    initialSelection?.symbol || null,
  );
  const [selectedUpdateSpeed, setSelectedUpdateSpeed] = useState(
    resolveUpdateSpeed(localStorage.getItem(UPDATE_SPEED_STORAGE_KEY)),
  );

  const categoryOptions = useMemo(() => {
    const categoryMap = stockOptions[selectedCountry?.value] || {};
    return Object.keys(categoryMap).map((categoryKey) => ({
      value: categoryKey,
      label: formatCoTraderFilterLabel(categoryKey),
    }));
  }, [selectedCountry?.value, stockOptions]);

  const symbolsForSelection = useMemo(
    () =>
      getSymbolsForSelection(
        selectedCountry?.value,
        selectedCategory?.value,
        stockOptions,
      ),
    [selectedCategory?.value, selectedCountry?.value, stockOptions],
  );
  useEffect(() => {
    if (!initialSelection) {
      setSelectedCountry(null);
      setSelectedCategory(null);
      setSelectedSymbolOption(null);
      return;
    }

    setSelectedCountry((current) => {
      const currentCountry = normalizeCountry(current?.value, stockOptions);
      return currentCountry
        ? { value: currentCountry, label: formatCoTraderFilterLabel(currentCountry) }
        : initialSelection.country;
    });
    setSelectedCategory((current) => current || initialSelection.category);
    setSelectedSymbolOption((current) => current || initialSelection.symbol);
  }, [initialSelection, stockOptions]);

  useEffect(() => {
    if (!selectedCountry?.value) {
      return;
    }

    const nextCategoryValue = resolveCategoryForCountry(
      selectedCountry.value,
      selectedCategory?.value,
      stockOptions,
    );

    if (nextCategoryValue !== selectedCategory?.value) {
      setSelectedCategory({
        value: nextCategoryValue,
        label: formatCoTraderFilterLabel(nextCategoryValue),
      });
      return;
    }

    const nextSymbol = resolveSymbolForSelection(
      selectedCountry.value,
      selectedCategory?.value,
      selectedSymbolOption?.value,
      stockOptions,
    );

    if (nextSymbol?.value !== selectedSymbolOption?.value) {
      setSelectedSymbolOption(nextSymbol);
    }
  }, [
    selectedCategory?.value,
    selectedCountry?.value,
    selectedSymbolOption?.value,
    stockOptions,
  ]);

  useEffect(() => {
    const nextSymbolValue = selectedSymbolOption?.value || "";
    const nextTickerLabel = selectedSymbolOption?.label || "";

    if (selectedCountry?.value) {
      localStorage.setItem(COUNTRY_STORAGE_KEY, selectedCountry.value);
    }
    if (selectedCategory?.value) {
      localStorage.setItem(CATEGORY_STORAGE_KEY, selectedCategory.value);
    }
    localStorage.setItem(UPDATE_SPEED_STORAGE_KEY, String(selectedUpdateSpeed.value));

    if (nextSymbolValue) {
      localStorage.setItem(SYMBOL_STORAGE_KEY, nextSymbolValue);
      setSelectedSymbol(nextSymbolValue);
      setSelectedTickerLabel(nextTickerLabel);
    }

    setUpdateSpeed(selectedUpdateSpeed.value);
    onSelectionContextChange?.({
      country: selectedCountry,
      category: selectedCategory,
      symbol: selectedSymbolOption,
      updateSpeed: selectedUpdateSpeed,
    });
  }, [
    onSelectionContextChange,
    selectedCategory,
    selectedCountry,
    selectedSymbolOption,
    selectedUpdateSpeed,
    setSelectedSymbol,
    setSelectedTickerLabel,
    setUpdateSpeed,
  ]);

  return (
    <div className="w-100">
      <div className="row g-2 align-items-center w-100">
        <div className="col-12 col-xl-2 col-lg-3 col-md-4">
          <Select
            options={countryOptions}
            value={selectedCountry}
            onChange={setSelectedCountry}
            isDisabled={isDisabled || isLoading || countryOptions.length === 0}
            menuPortalTarget={document.body}
            styles={selectPortalStyles}
            components={autoScrollSelectComponents}
          />
        </div>

        <div className="col-12 col-xl-2 col-lg-3 col-md-4">
          <Select
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
            isDisabled={isDisabled || isLoading || categoryOptions.length === 0}
            menuPortalTarget={document.body}
            styles={selectPortalStyles}
            components={autoScrollSelectComponents}
          />
        </div>

        <div className="col-12 col-xl-3 col-lg-3 col-md-4">
          <Select
            options={symbolsForSelection}
            value={selectedSymbolOption}
            onChange={setSelectedSymbolOption}
            isDisabled={isDisabled || isLoading || symbolsForSelection.length === 0}
            menuPortalTarget={document.body}
            styles={selectPortalStyles}
            placeholder={symbolsForSelection.length ? "Select symbol" : "No symbols"}
            components={autoScrollSelectComponents}
          />
        </div>

        <div className="col-12 col-xl-1 col-lg-2 col-md-4">
          <Select
            options={UPDATE_SPEED_OPTIONS}
            value={selectedUpdateSpeed}
            onChange={setSelectedUpdateSpeed}
            isDisabled={isDisabled || isLoading}
            menuPortalTarget={document.body}
            styles={selectPortalStyles}
            components={autoScrollSelectComponents}
          />
        </div>
      </div>
    </div>
  );
};

export default CoTraderFilterStocks;
